import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime, formatFileSize } from "@/lib/clients";
import { fileCategory, openFile, downloadFile, DOC_TYPES, DEFAULT_DOC_TYPE, docTypeLabel, docTypeColor } from "@/lib/files";
import { FileThumb } from "@/components/FileThumb";
import { Search, ExternalLink, Download, FolderOpen, ArrowUpRight, CloudUpload, Loader2 } from "lucide-react";
import { toast } from "sonner";

const FORMAT_TYPES = [
  { value: "all", label: "All formats" },
  { value: "image", label: "Images" },
  { value: "pdf", label: "PDFs" },
  { value: "document", label: "Documents" },
  { value: "spreadsheet", label: "Spreadsheets" },
  { value: "email", label: "Emails" },
  { value: "other", label: "Other" },
];

const SORT_OPTIONS = [
  { value: "date_desc", label: "Newest first" },
  { value: "date_asc", label: "Oldest first" },
  { value: "client_asc", label: "Client (A–Z)" },
  { value: "client_desc", label: "Client (Z–A)" },
  { value: "type_asc", label: "Category (A–Z)" },
];

export default function Documents() {
  const [files, setFiles] = useState([]);
  const [search, setSearch] = useState("");
  const [format, setFormat] = useState("all");
  const [category, setCategory] = useState("all");
  const [sortBy, setSortBy] = useState("date_desc");
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (category !== "all") params.doc_type = category;
    const { data } = await api.get("/files", { params });
    setFiles(data);
    setLoading(false);
  }, [search, category]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const filtered = files.filter((f) => format === "all" || fileCategory(f.original_filename, f.content_type) === format);

  const visible = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case "date_asc": return (a.created_at || "").localeCompare(b.created_at || "");
      case "client_asc": return (a.client_name || "").localeCompare(b.client_name || "");
      case "client_desc": return (b.client_name || "").localeCompare(a.client_name || "");
      case "type_asc": return docTypeLabel(a.doc_type).localeCompare(docTypeLabel(b.doc_type));
      default: return (b.created_at || "").localeCompare(a.created_at || "");
    }
  });

  const handleOpen = async (f) => { try { await openFile(f.id, f.original_filename); } catch { toast.error("Could not open file"); } };
  const openDoc = (f) => {
    if (f.sp_web_url && !f.sp_link_deleted) window.open(f.sp_web_url, "_blank", "noopener,noreferrer");
    else handleOpen(f);
  };
  const handleDownload = async (f) => { try { await downloadFile(f.id, f.original_filename); } catch { toast.error("Could not download file"); } };

  const legacyCount = files.filter((f) => !f.sp_web_url && !f.sp_link_deleted).length;
  const migrate = async () => {
    setMigrating(true);
    try {
      const { data } = await api.post("/files/migrate-legacy");
      const msg = `Migrated ${data.migrated} file${data.migrated === 1 ? "" : "s"} to SharePoint` +
        (data.skipped ? `, ${data.skipped} skipped` : "") + (data.failed ? `, ${data.failed} failed` : "");
      data.failed ? toast.warning(msg) : toast.success(data.migrated ? msg : "No legacy files to migrate");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Migration failed");
    } finally { setMigrating(false); }
  };

  const selectCls = "border border-gray-300 rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]";

  return (
    <div className="p-6 lg:p-8" data-testid="documents-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter text-gray-900">All Documents</h1>
          <p className="text-gray-500 text-sm mt-1">{visible.length} file{visible.length !== 1 ? "s" : ""} across all clients</p>
        </div>
        {user?.role === "admin" && legacyCount > 0 && (
          <button onClick={migrate} disabled={migrating} data-testid="migrate-legacy-button"
            title="Move older files into SharePoint so they open in SharePoint too"
            className="inline-flex items-center gap-1.5 bg-white border border-gray-300 hover:border-[#0078D4] hover:text-[#0078D4] text-gray-600 px-3 py-2 rounded-sm font-semibold text-sm transition-colors disabled:opacity-60">
            {migrating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudUpload className="w-4 h-4" />}
            {migrating ? "Migrating…" : `Migrate ${legacyCount} legacy file${legacyCount === 1 ? "" : "s"} to SharePoint`}
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input data-testid="documents-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by file name…"
            className="w-full border border-gray-300 rounded-sm pl-9 pr-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        </div>
        <select data-testid="documents-category-filter" value={category} onChange={(e) => setCategory(e.target.value)} className={selectCls}>
          <option value="all">All categories</option>
          {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select data-testid="documents-format-filter" value={format} onChange={(e) => setFormat(e.target.value)} className={selectCls}>
          {FORMAT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select data-testid="documents-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={selectCls}>
          {SORT_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden shadow-sm">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {["File", "Category", "Client", "Uploaded by", "Size", "Date", ""].map((h) => (
                <th key={h || "actions"} className="text-xs font-bold uppercase tracking-wider text-gray-500 py-3 px-4">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="documents-table-body">
            {loading ? (
              <tr><td colSpan={7} className="py-12 text-center text-sm text-gray-400">Loading…</td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={7} className="py-16 text-center">
                <FolderOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-sm text-gray-500 font-medium">No documents found</p>
                <p className="text-xs text-gray-400 mt-1">Upload files from a client's detail page.</p>
              </td></tr>
            ) : visible.map((f) => {
              const dt = f.doc_type || DEFAULT_DOC_TYPE;
              return (
                <tr key={f.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors" data-testid={`document-row-${f.id}`}>
                  <td className="py-3 px-4">
                    <button type="button" onClick={() => openDoc(f)} data-testid={`document-link-${f.id}`}
                      title={f.sp_web_url ? "Open in SharePoint" : "Open"}
                      className="flex items-center gap-3 text-left group/doc">
                      <FileThumb file={f} size="w-8 h-8" />
                      <span className="text-sm font-semibold text-gray-900 truncate max-w-[260px] group-hover/doc:text-[#0078D4] group-hover/doc:underline">{f.original_filename}</span>
                    </button>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-sm whitespace-nowrap ${docTypeColor(dt)}`}
                      data-testid={`document-category-${f.id}`}>{docTypeLabel(dt)}</span>
                  </td>
                  <td className="py-3 px-4">
                    <button onClick={() => navigate(`/clients/${f.client_id}`)} data-testid={`document-client-${f.id}`}
                      className="inline-flex items-center gap-1 text-sm font-medium text-[#002FA7] hover:underline">
                      {f.client_name} <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600">{f.uploaded_by || "—"}</td>
                  <td className="py-3 px-4 text-sm text-gray-600">{formatFileSize(f.size)}</td>
                  <td className="py-3 px-4 text-sm text-gray-500">{formatDateTime(f.created_at)}</td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleOpen(f)} title="Open in default app" data-testid={`document-open-${f.id}`}
                        className="p-1.5 rounded-sm text-gray-400 hover:text-[#002FA7] hover:bg-blue-50">
                        <ExternalLink className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDownload(f)} title="Download" data-testid={`document-download-${f.id}`}
                        className="p-1.5 rounded-sm text-gray-400 hover:text-[#002FA7] hover:bg-blue-50">
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
